import { CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Injector, NgZone, ViewChild, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Rxjs
import { filter, take } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';
import { CookieService } from 'ngx-cookie-service';
import { MarketingEventEnumeration, MarketingEventService } from '@shared/services';

// Models
import { SimulatorType } from '@credit/simulator/models';

// Enum
import { ProductType } from '@core/model/enums';

// Environments
import { environment } from '@environments/environment';

interface SimulatorItemsType {
  code: string;
  base: number;
  value: number;
}
interface SimulationOutputType extends SimulatorItemsType {
  items: SimulatorItemsType[];
  timeValue: string | Date;
}

interface ApplicationConfigType {
  applicationAmount: number;
  applicationTerm: number;
  applicationQuota: number;
  applicationLimitPaymentDate: number;
}

@Component({
  selector: 'app-rapiflex-now',
  templateUrl: './rapiflex-now.component.html',
  styleUrls: ['./rapiflex-now.component.scss'],
})
export class RapiflexNowComponent implements OnInit, AfterViewInit {
  @ViewChild('simulatorRef') public simulatorRef!: ElementRef;
  private simulatorRefInstance: SimulatorType;

  public isSimulatorLoading: boolean;
  public isItemsLoading: boolean;
  private total: number;
  private fees: SimulatorItemsType[];
  private applicationConfig: ApplicationConfigType;

  private readonly webElementService: WebcomponentsService;
  private readonly ngZone: NgZone;
  private readonly router: Router;
  private readonly cookieService: CookieService;
  private readonly currencyPipe: CurrencyPipe;
  private readonly authService: AuthService;
  private readonly marketingEventService: MarketingEventService;

  private readonly domain: string;

  constructor(private readonly injector: Injector) {
    this.webElementService = this.injector.get(WebcomponentsService);
    this.ngZone = this.injector.get(NgZone);
    this.router = this.injector.get(Router);
    this.cookieService = this.injector.get(CookieService);
    this.currencyPipe = this.injector.get(CurrencyPipe);
    this.authService = this.injector.get(AuthService);
    this.marketingEventService = this.injector.get(MarketingEventService);
    this.isSimulatorLoading = true;
    this.isItemsLoading = true;
    this.total = 0;
    this.fees = [];
    this.applicationConfig = {} as ApplicationConfigType;
    this.domain = environment.domain;
  }

  public ngOnInit(): void {
    this.clearStoredData();
  }

  public ngAfterViewInit(): void {
    this.renderSimulatorWidget();
  }

  private renderSimulatorWidget(): void {
    const token = this.authService?.token;
    const payload = this.authService?.getTokenData(token);
    this.webElementService.isLoaded$.pipe(filter(Boolean), take(1)).subscribe({
      next: () => {
        const simulatorConfigurationObj = {
          clientId: payload.clienteId,
          productCode: ProductType.RFYA,
          isRecurrentCustomer: true,
          runAfterLoading: (simulationOutput: SimulationOutputType) =>
            this.runAfterLoadingSimulatorWidget(simulationOutput),
          runWhenLoadingItems: (isLoadingSimulate: boolean) => {
            this.ngZone.run(() => {
              this.isItemsLoading = isLoadingSimulate;
            });
          },
        };

        this.simulatorRefInstance = this.simulatorRef?.nativeElement?._ngElementStrategy?.componentRef?.instance;
        this.simulatorRefInstance?.buildSimulator(simulatorConfigurationObj);
      },
    });
  }

  public get getFees(): string {
    const discountValue = this.fees
      .filter((item) => item.value < 0)
      .reduce((previousValue, item) => previousValue + item.value, 0);
    return this.currencyPipe.transform(Math.abs(discountValue), 'COP', 'symbol', '1.0-0');
  }

  private clearStoredData(): void {
    const keys = ['applicationAmount', 'applicationTerm', 'applicationQuota', 'applicationLimitPaymentDate'];
    keys.forEach((key) => {
      this.cookieService.delete(key, '/', this.domain);
      localStorage.removeItem(key);
    });
  }

  private runAfterLoadingSimulatorWidget(simulationOutput: SimulationOutputType): void {
    const timeValueInput = simulationOutput.timeValue;
    const timeValueString = typeof timeValueInput === 'string' ? timeValueInput : timeValueInput.toString();

    let timeValueCleaned = timeValueString;
    if (timeValueCleaned.endsWith(')')) {
      const openIdx = timeValueCleaned.lastIndexOf('(');
      if (openIdx !== -1) {
        timeValueCleaned = timeValueCleaned.slice(0, openIdx).trim();
      }
    }

    const simulationDateParsed = new Date(timeValueCleaned);
    const simulationEpochTime = simulationDateParsed.getTime();
    const transformToSeconds = Math.floor(simulationEpochTime / 1000);

    this.fees = simulationOutput.items;

    const today = new Date();
    const daysBetweenDates = this.getDaysBetweenDatesIgnoringTime(today, simulationDateParsed);

    const getTotalAmount = this.fees.map((item) => item.value).reduce((sum, v) => sum + v, 0);
    const simulation = this.simulatorRefInstance?.getSimulation?.();
    const amountValue = simulation?.amountValue ?? simulationOutput.base;

    this.total = getTotalAmount;
    this.applicationConfig.applicationAmount = amountValue;
    this.applicationConfig.applicationQuota = getTotalAmount;
    this.applicationConfig.applicationTerm = daysBetweenDates;
    this.applicationConfig.applicationLimitPaymentDate = transformToSeconds;
    this.isSimulatorLoading = false;
  }

  private setCookiesAndStorage(key: string, value: number | string | Date): void {
    if (value === undefined || value === null) return;
    this.cookieService.set(key, value.toString(), 1, '/', this.domain);
    sessionStorage.setItem(key, value.toString());
  }

  private getDaysBetweenDatesIgnoringTime(startDate: Date, endDate: Date): number {
    const epochTimeDays = 1000 * 60 * 60 * 24;

    const startMidnight = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endMidnight = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    return Math.floor((endMidnight.getTime() - startMidnight.getTime()) / epochTimeDays);
  }

  public confirm(): void {
    this.setCookiesAndStorage('applicationAmount', this.applicationConfig.applicationAmount);
    this.setCookiesAndStorage('applicationTerm', this.applicationConfig.applicationTerm);
    this.setCookiesAndStorage('applicationQuota', this.applicationConfig.applicationQuota);
    this.setCookiesAndStorage('applicationLimitPaymentDate', this.applicationConfig.applicationLimitPaymentDate);
    this.marketingEventService.sendMarketingEvents(MarketingEventEnumeration.LOAN_APPLICATION_SIMULATE_RFYA);
    this.router.navigate(['application-terms/']);
  }
}
